/**
 * MyActivities — Sprint 2 / TASK 1-7 refactor + TASK 4-1 versioning.
 *
 * Presentation only — the data fetch (`getJobApplicationsPaginated`),
 * preview content lookup, reminder send flow, and JobApplication shape
 * are all preserved verbatim. This file only changes how the rows are
 * rendered: the legacy hand-rolled table → the new generic <DataTable>
 * (TASK 1-5) with filter chips for Outcome, Date range, Score band.
 *
 * TASK 4-1 additions:
 *   • Load `optimization_history.outcome`, `.id` (for parent_id) and
 *     `.outcome_set_at` alongside each activity row, matched by
 *     `optimization_id`.
 *   • Outcome column → inline <select> for rows linked to an optimization.
 *     On change: PATCH `optimization_history.outcome` + `outcome_set_at`;
 *     show a "Recommendations updating…" toast; fire `upsert-profile-vector`
 *     best-effort (non-blocking).
 *   • Actions column → "Iterate" button for optimized rows: stores the
 *     `optimization_history.id` in sessionStorage as `optimizer_parent_id`
 *     and navigates to the optimizer so the user can create a v2.
 *
 * Hard rules (TASK 4-1):
 *   • Outcome changes are audit-logged via `outcome_set_at`.
 *   • Vector recalibration is best-effort — UI must not block on its result.
 *
 * On `<md` viewports DataTable's table becomes a card-list (rendered
 * here, since the shared component keeps a single layout); on ≥md it
 * shows the Mobbin/Deel-density table.
 */
import {
  Activity,
  AlertCircle,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Send,
  GitBranch,
  Mail,
  Share2,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { DataTable, FilterChips } from '../../../components/DataTable';
import type { Column, FilterDef } from '../../../components/DataTable';
import { supabase } from '../../../lib/supabase';
import {
  getJobApplicationsPaginated,
} from '../../../services/applicationTrackingService';
import type { JobApplication } from '../../../services/applicationTrackingService';

import type { DashboardPage } from './Layout';

interface MyActivitiesProps {
  onNavigate?: (page: DashboardPage) => void;
}

// Fetch a single, generously-sized page so DataTable can paginate and
// filter client-side. Server-side pagination would fight the chip
// filters; users with hundreds of applications still load comfortably
// inside this cap (and DataTable shows a footer 'N of M' counter).
const ROW_FETCH_LIMIT = 500;

// ─────────────────────────────────────────────────────────────────────
// Outcome type (mirrors the DB CHECK constraint added in TASK 4-1).
// ─────────────────────────────────────────────────────────────────────

type OptimizationOutcome = 'unknown' | 'applied' | 'interview' | 'offer' | 'rejected';

const OUTCOME_OPTIONS: { value: OptimizationOutcome; label: string }[] = [
  { value: 'unknown',   label: 'Unknown' },
  { value: 'applied',   label: 'Applied' },
  { value: 'interview', label: 'Interview' },
  { value: 'offer',     label: 'Offer' },
  { value: 'rejected',  label: 'Rejected' },
];

const OUTCOME_COLOR: Record<OptimizationOutcome, string> = {
  unknown:   'var(--ds-text-muted)',
  applied:   'var(--ds-brand)',
  interview: 'var(--ds-warn)',
  offer:     'var(--ds-ok)',
  rejected:  'var(--ds-danger)',
};

// ─────────────────────────────────────────────────────────────────────
// Row shape consumed by DataTable. We keep `raw` so action cells can
// invoke the original handlers without changing column data.
// ─────────────────────────────────────────────────────────────────────

type ActivityRow = {
  id: string;
  date: string;                     // ISO timestamp (created_at)
  job: string;                      // job_title
  company: string;                  // company_name (rendered as secondary line)
  action: 'Optimized' | 'Applied';
  score: number | null;             // confidence_score (0-100), null when unknown
  outcome: string;                  // human-readable status label (application_status)
  outcomeKey: string;               // raw status used for filter chip
  /** optimization_history primary key — present only if row has optimization_id */
  optimHistoryId: string | null;
  /** optimization_history.optimization_id FK */
  optimizationId: string | null;
  /** TASK 4-1 outcome for the optimization (separate from application_status) */
  optimOutcome: OptimizationOutcome | null;
  raw: JobApplication;
} & Record<string, unknown>;

// Match the existing legacy mapping in the previous version of this
// file so translated outcome chips stay identical.
const STATUS_LABEL_KEYS: Record<string, string> = {
  applied: 'dashboard.myActivities.statusApplied',
  viewed: 'dashboard.myActivities.statusViewed',
  interview_scheduled: 'dashboard.myActivities.statusInterviewScheduled',
  interview_completed: 'dashboard.myActivities.statusInterviewCompleted',
  offered: 'dashboard.myActivities.statusOffered',
  not_applied: 'dashboard.myActivities.statusNotApplied',
  saved_for_later: 'dashboard.myActivities.statusSavedForLater',
  pending: 'dashboard.myActivities.statusPending',
};

const SCORE_BAND_KEYS = ['high', 'mid', 'low', 'none'] as const;
type ScoreBandKey = typeof SCORE_BAND_KEYS[number];

const DATE_RANGE_KEYS = ['7d', '30d', '90d', 'older'] as const;
type DateRangeKey = typeof DATE_RANGE_KEYS[number];

function classifyScore(score: number | null | undefined): ScoreBandKey {
  if (score == null) return 'none';
  if (score >= 80) return 'high';
  if (score >= 50) return 'mid';
  return 'low';
}

function classifyDate(iso: string | null | undefined, now: number): DateRangeKey {
  if (!iso) return 'older';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'older';
  const days = (now - t) / (24 * 60 * 60 * 1000);
  if (days <= 7) return '7d';
  if (days <= 30) return '30d';
  if (days <= 90) return '90d';
  return 'older';
}

function scoreTone(score: number): string {
  const band = classifyScore(score);
  if (band === 'high') return 'var(--ds-ok)';
  if (band === 'mid') return 'var(--ds-warn)';
  return 'var(--ds-danger)';
}

// ─────────────────────────────────────────────────────────────────────
// Toast state (lightweight in-component notification)
// ─────────────────────────────────────────────────────────────────────

interface ToastMsg {
  id:      number;
  message: string;
  kind:    'info' | 'success' | 'error';
}

// ─────────────────────────────────────────────────────────────────────
// Outcome save + vector recalibration (TASK 4-1)
// ─────────────────────────────────────────────────────────────────────

async function persistOutcome(
  optimHistoryId: string,
  outcome: OptimizationOutcome,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('optimization_history')
    .update({
      outcome,
      outcome_set_at: new Date().toISOString(),
    })
    .eq('id', optimHistoryId);

  return { error: error?.message ?? null };
}

/** Best-effort profile vector recalibration — never blocks the caller. */
function triggerVectorRecalibration(): void {
  void supabase.functions.invoke('upsert-profile-vector', { body: {} })
    .then(({ error }) => {
      if (error) {
        console.warn('[MyActivities] upsert-profile-vector error (best-effort):', error.message);
      }
    })
    .catch((e: unknown) => {
      console.warn('[MyActivities] upsert-profile-vector threw (best-effort):', (e as Error).message);
    });
}

// ─────────────────────────────────────────────────────────────────────
// Load optimization_history rows for a set of optimization_ids
// ─────────────────────────────────────────────────────────────────────

interface OptimHistoryRow {
  id: string;
  optimization_id: string;
  outcome: OptimizationOutcome | null;
}

async function fetchOptimHistory(
  optimizationIds: string[],
): Promise<OptimHistoryRow[]> {
  if (optimizationIds.length === 0) return [];
  const { data, error } = await supabase
    .from('optimization_history')
    .select('id, optimization_id, outcome')
    .in('optimization_id', optimizationIds);

  if (error) {
    console.warn('[MyActivities] fetchOptimHistory error:', error.message);
    return [];
  }
  return (data ?? []) as OptimHistoryRow[];
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export default function MyActivities({ onNavigate }: MyActivitiesProps) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * Drawer state — when set, opens a right-side slide-in panel with all three
   * artifacts (Resume, Cover Letter, Application Note) as tabs. The `activeTab`
   * remembers which tab the user opened so clicking different action icons
   * lands them on the right tab immediately. Job title shown in the drawer header.
   */
  const [preview, setPreview] = useState<{
    jobTitle: string;
    company: string;
    activeTab: 'resume' | 'cover' | 'application';
    resume: string | null;
    cover: string | null;
    application: string | null;
    loadingResume: boolean;
  } | null>(null);
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const toastSeq = useRef(0);

  // Per-row outcome state — keyed by optimHistoryId.
  // Populated from loaded optimization_history rows, then updated optimistically on change.
  const [outcomeMap, setOutcomeMap] = useState<Record<string, OptimizationOutcome>>({});
  const [savingOutcome, setSavingOutcome] = useState<string | null>(null);

  const pushToast = useCallback(
    (message: string, kind: ToastMsg['kind'] = 'info') => {
      const id = ++toastSeq.current;
      setToasts((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3_500);
    },
    [],
  );

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Data load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getJobApplicationsPaginated(ROW_FETCH_LIMIT, 0);
      const apps = result.data;

      // Collect all optimization_ids to batch-fetch optimization_history.
      const optimizationIds = apps
        .map((a) => a.optimization_id)
        .filter((id): id is string => !!id);

      // Batch-load optimization_history.
      const optimRows = await fetchOptimHistory(optimizationIds);
      const optimByOptId = new Map<string, OptimHistoryRow>();
      for (const r of optimRows) {
        optimByOptId.set(r.optimization_id, r);
      }

      // Build initial outcomeMap from DB values.
      const initialOutcomeMap: Record<string, OptimizationOutcome> = {};
      for (const r of optimRows) {
        initialOutcomeMap[r.id] = r.outcome ?? 'unknown';
      }
      setOutcomeMap(initialOutcomeMap);

      const mapped = apps.map<ActivityRow>((app) => {
        const status = app.application_status ?? 'pending';
        const labelKey = STATUS_LABEL_KEYS[status.toLowerCase()];
        const outcome = labelKey
          ? t(labelKey)
          : status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

        const optimRow = app.optimization_id
          ? (optimByOptId.get(app.optimization_id) ?? null)
          : null;

        return {
          id: app.id,
          date: app.created_at,
          job: app.job_title || '—',
          company: app.company_name ?? '—',
          action: app.optimization_id ? 'Optimized' : 'Applied',
          score: typeof app.confidence_score === 'number' ? app.confidence_score : null,
          outcome,
          outcomeKey: status.toLowerCase(),
          optimHistoryId: optimRow?.id ?? null,
          optimizationId: app.optimization_id ?? null,
          optimOutcome: optimRow?.outcome ?? null,
          raw: app,
        };
      });
      setRows(mapped);
    } catch (err) {
      console.error('[MyActivities] Load error:', err);
      setError(err instanceof Error ? err.message : t('dashboard.myActivities.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Outcome change handler (TASK 4-1) ─────────────────────────────
  const handleOutcomeChange = useCallback(
    async (optimHistoryId: string, newOutcome: OptimizationOutcome) => {
      // Optimistic update.
      setOutcomeMap((prev) => ({ ...prev, [optimHistoryId]: newOutcome }));
      setSavingOutcome(optimHistoryId);

      const { error: saveErr } = await persistOutcome(optimHistoryId, newOutcome);
      setSavingOutcome(null);

      if (saveErr) {
        pushToast('Could not save outcome. Please try again.', 'error');
        // Reload to restore the correct value.
        void load();
        return;
      }

      pushToast('Recommendations updating…', 'info');
      triggerVectorRecalibration();
    },
    [load, pushToast],
  );

  // ── Iterate: open optimizer with parent_id preset (TASK 4-1) ──────
  const handleIterate = useCallback(
    (row: ActivityRow) => {
      if (!row.optimHistoryId) return;
      try {
        sessionStorage.setItem('optimizer_parent_id', row.optimHistoryId);
        if (row.optimizationId) {
          sessionStorage.setItem('history_apply_optimization_id', row.optimizationId);
        }
      } catch {
        // ignore
      }
      // Iterate / re-optimize now goes through the AI Agent (the legacy
      // single-optimize flow has been retired).
      if (onNavigate) {
        onNavigate('agent');
      } else {
        window.dispatchEvent(
          new CustomEvent('jobs:navigate', { detail: { page: 'agent' } }),
        );
      }
    },
    [onNavigate],
  );

  // ── Preview handlers (unchanged data path) ────────────────────────

  /**
   * Load all three artifacts (resume / cover letter / application note) for
   * a single application and open the drawer on the requested tab. Resume is
   * loaded async from llm_optimization_level1/level2 since it can be large.
   * Cover letter and application note are stored inline on job_applications.
   */
  const openArtifactDrawer = useCallback(
    async (app: JobApplication, tab: 'resume' | 'cover' | 'application') => {
      const cover =
        (app as { cover_letter_snippet?: string }).cover_letter_snippet ||
        app.cover_letter_version ||
        null;
      const application =
        (app as { application_snippet?: string }).application_snippet ||
        null;

      // Prefer the saved resume_version on the row (newer agent flow), fall back
      // to fetching from llm_optimization tables (older multi-step flow).
      const inlineResume = (app as { resume_version?: string }).resume_version || null;

      setPreview({
        jobTitle: app.job_title || '—',
        company: app.company_name || '',
        activeTab: tab,
        resume: inlineResume,
        cover,
        application,
        loadingResume: !inlineResume,
      });

      if (inlineResume) return;

      // Fetch optimized resume async from optimization tables
      const rid = (app as { resume_version_id?: string }).resume_version_id;
      if (!rid) {
        setPreview((p) => p ? { ...p, loadingResume: false } : p);
        return;
      }
      try {
        const { data: opt2 } = await supabase
          .from('llm_optimization_level2')
          .select('optimized_resume, final_resume')
          .eq('optimization_id', rid)
          .single();
        let text = (opt2?.optimized_resume || opt2?.final_resume || '') as string;
        if (!text) {
          const { data: opt1 } = await supabase
            .from('llm_optimization_level1')
            .select('optimized_resume, final_resume')
            .eq('optimization_id', rid)
            .single();
          text = (opt1?.optimized_resume || opt1?.final_resume || '') as string;
        }
        setPreview((p) => p ? { ...p, resume: text || null, loadingResume: false } : p);
      } catch {
        setPreview((p) => p ? { ...p, loadingResume: false } : p);
      }
    },
    [],
  );

  const handlePreviewResume = (app: JobApplication) => openArtifactDrawer(app, 'resume');
  const handlePreviewCover = (app: JobApplication) => { void openArtifactDrawer(app, 'cover'); };
  const handlePreviewApplication = (app: JobApplication) => { void openArtifactDrawer(app, 'application'); };

  const sendReminderEmail = async (applicationId: string) => {
    setSendingReminder(applicationId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-apply-reminder`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ application_id: applicationId }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && (json as { success?: boolean }).success) {
        await load();
      } else {
        console.error('Send reminder failed:', json);
      }
    } finally {
      setSendingReminder(null);
    }
  };

  // ── Client-side filtering driven by FilterChips ───────────────────
  const filteredRows = useMemo(() => {
    const outcome   = filterValues.outcome ?? [];
    const dateRange = filterValues.dateRange ?? [];
    const scoreBand = filterValues.score ?? [];
    const action    = filterValues.action ?? [];
    if (
      outcome.length === 0 &&
      dateRange.length === 0 &&
      scoreBand.length === 0 &&
      action.length === 0
    ) {
      return rows;
    }
    const now = Date.now();
    return rows.filter((row) => {
      if (outcome.length && !outcome.includes(row.outcomeKey)) return false;
      if (action.length && !action.includes(row.action.toLowerCase())) return false;
      if (dateRange.length && !dateRange.includes(classifyDate(row.date, now))) return false;
      if (scoreBand.length && !scoreBand.includes(classifyScore(row.score))) return false;
      return true;
    });
  }, [rows, filterValues]);

  // Filter definitions for the chip strip — Outcome / Date / Score / Action.
  const filters = useMemo<FilterDef[]>(() => [
    {
      id: 'outcome',
      label: t('dashboard.myActivities.outcomeFilter', 'Outcome'),
      options: [
        { value: 'applied',              label: t('dashboard.myActivities.statusApplied') },
        { value: 'viewed',               label: t('dashboard.myActivities.statusViewed') },
        { value: 'interview_scheduled',  label: t('dashboard.myActivities.statusInterviewScheduled') },
        { value: 'interview_completed',  label: t('dashboard.myActivities.statusInterviewCompleted') },
        { value: 'offered',              label: t('dashboard.myActivities.statusOffered') },
        { value: 'not_applied',          label: t('dashboard.myActivities.statusNotApplied') },
        { value: 'pending',              label: t('dashboard.myActivities.statusPending') },
      ],
    },
    {
      id: 'dateRange',
      label: t('dashboard.myActivities.dateRangeFilter', 'Date range'),
      options: [
        { value: '7d',    label: t('dashboard.myActivities.dateLast7d',  'Last 7 days') },
        { value: '30d',   label: t('dashboard.myActivities.dateLast30d', 'Last 30 days') },
        { value: '90d',   label: t('dashboard.myActivities.dateLast90d', 'Last 90 days') },
        { value: 'older', label: t('dashboard.myActivities.dateOlder',   'Older') },
      ],
    },
    {
      id: 'score',
      label: t('dashboard.myActivities.scoreBandFilter', 'Score band'),
      options: [
        { value: 'high', label: t('dashboard.myActivities.scoreHigh', 'High (80+)') },
        { value: 'mid',  label: t('dashboard.myActivities.scoreMid',  'Medium (50–79)') },
        { value: 'low',  label: t('dashboard.myActivities.scoreLow',  'Low (<50)') },
        { value: 'none', label: t('dashboard.myActivities.scoreNone', 'No score') },
      ],
    },
    {
      id: 'action',
      label: t('dashboard.myActivities.actionFilter', 'Action'),
      options: [
        { value: 'optimized', label: t('dashboard.myActivities.actionOptimized', 'Optimized') },
        { value: 'applied',   label: t('dashboard.myActivities.actionApplied',   'Applied') },
      ],
    },
  ], [t]);

  // ── Column definitions ────────────────────────────────────────────

  const columns = useMemo<Column<ActivityRow>[]>(() => [
    {
      key: 'date',
      header: t('dashboard.myActivities.date'),
      minWidth: 120,
      cell: (value) =>
        value ? new Date(String(value)).toLocaleDateString(i18n.language) : '—',
    },
    {
      key: 'job',
      header: t('dashboard.myActivities.job'),
      minWidth: 220,
      cell: (_value, row) => (
        <div className="flex flex-col min-w-0">
          <span
            className="t-card-title truncate"
            title={row.job}
          >
            {row.job}
          </span>
          <span
            className="t-meta truncate"
            title={row.company}
          >
            {row.company}
          </span>
        </div>
      ),
    },
    {
      key: 'action',
      header: t('dashboard.myActivities.actionFilter', 'Action'),
      minWidth: 120,
      cell: (_value, row) => (
        <span
          className={row.action === 'Optimized' ? 'ds-chip ds-chip-brand' : 'ds-chip'}
        >
          {row.action === 'Optimized'
            ? t('dashboard.myActivities.actionOptimized', 'Optimized')
            : t('dashboard.myActivities.actionApplied', 'Applied')}
        </span>
      ),
    },
    {
      key: 'score',
      header: t('dashboard.myActivities.scoreColumn', 'Score'),
      minWidth: 96,
      cell: (_value, row) => {
        if (row.score == null) return <span className="t-meta">—</span>;
        return (
          <span
            className="t-card-title t-num inline-flex items-center gap-1"
            style={{ color: scoreTone(row.score) }}
          >
            {Math.round(row.score)}
          </span>
        );
      },
    },
    {
      // TASK 4-1: for optimized rows → inline outcome dropdown.
      // For non-optimized rows → static status badge (unchanged).
      key: 'outcome',
      header: t('dashboard.myActivities.outcomeFilter', 'Outcome'),
      minWidth: 180,
      sortable: false,
      cell: (_value, row) => {
        if (row.optimHistoryId) {
          const currentOutcome = outcomeMap[row.optimHistoryId] ?? 'unknown';
          const isSaving = savingOutcome === row.optimHistoryId;
          return (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0 transition-colors"
                style={{ background: OUTCOME_COLOR[currentOutcome] }}
                aria-hidden
              />
              <select
                value={currentOutcome}
                disabled={isSaving}
                aria-label={`Outcome for ${row.job}`}
                onChange={(e) =>
                  void handleOutcomeChange(
                    row.optimHistoryId!,
                    e.target.value as OptimizationOutcome,
                  )
                }
                className="ds-select w-auto disabled:opacity-50 disabled:cursor-wait"
                style={{ maxWidth: 130 }}
              >
                {OUTCOME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {isSaving && (
                <LoadingSpinner size="xs" showText={false} />
              )}
            </div>
          );
        }

        // Static outcome badge for non-optimized rows.
        const isPositive = ['offered', 'interview_scheduled', 'interview_completed'].includes(row.outcomeKey);
        const isApplied  = ['applied', 'viewed'].includes(row.outcomeKey);
        const dotColor   = isPositive ? 'var(--ds-ok)' : isApplied ? 'var(--ds-brand)' : 'var(--ds-text-muted)';
        return (
          <span className="ds-chip">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dotColor }} aria-hidden />
            <span className="truncate">{row.outcome}</span>
          </span>
        );
      },
    },
    {
      key: 'id', // virtual cell — we use `id` so the value is non-null
      header: t('dashboard.myActivities.actionsColumn', 'Actions'),
      minWidth: 240,
      sortable: false,
      cell: (_value, row) => {
        const app = row.raw;
        const hasReminder = (app as { reminder_date?: string }).reminder_date != null;
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => void handlePreviewResume(app)}
              className="ds-btn ds-btn-ghost ds-btn-sm min-w-[32px] touch-manipulation"
              title={t('dashboard.myActivities.previewResume')}
              aria-label={t('dashboard.myActivities.previewResume')}
            >
              <Eye className="w-4 h-4 t-muted" />
            </button>
            <button
              type="button"
              onClick={() => handlePreviewCover(app)}
              className="ds-btn ds-btn-ghost ds-btn-sm min-w-[32px] touch-manipulation"
              title={t('dashboard.myActivities.previewCoverLetter')}
              aria-label={t('dashboard.myActivities.previewCoverLetter')}
            >
              <Mail className="w-4 h-4 t-muted" />
            </button>
            <button
              type="button"
              onClick={() => handlePreviewApplication(app)}
              className="ds-btn ds-btn-ghost ds-btn-sm min-w-[32px] touch-manipulation"
              title={t('dashboard.myActivities.previewApplication')}
              aria-label={t('dashboard.myActivities.previewApplication')}
            >
              <Share2 className="w-4 h-4 t-muted" />
            </button>

            {/* TASK 4-1: Iterate button — opens optimizer with parent_id preset. */}
            {row.optimHistoryId && (
              <button
                type="button"
                onClick={() => handleIterate(row)}
                className="ds-btn ds-btn-ghost ds-btn-sm min-w-[32px] touch-manipulation"
                title="Iterate — create a v2 optimized resume"
                aria-label="Iterate — create a v2 optimized resume"
              >
                <GitBranch className="w-4 h-4 t-brand" />
              </button>
            )}

            {hasReminder && (
              <button
                type="button"
                disabled={!!sendingReminder}
                onClick={() => void sendReminderEmail(app.id)}
                className="ds-btn ds-btn-ghost ds-btn-sm min-w-[32px] touch-manipulation"
                title={t('dashboard.myActivities.sendReminderNow')}
                aria-label={t('dashboard.myActivities.sendReminderNow')}
              >
                {sendingReminder === app.id
                  ? <LoadingSpinner size="xs" showText={false} />
                  : <Mail className="w-4 h-4 t-brand" />}
              </button>
            )}
            {app.job_url && (
              <a
                href={app.job_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ds-btn ds-btn-ghost ds-btn-sm min-w-[32px] touch-manipulation"
                title={t('dashboard.myActivities.openJob')}
                aria-label={t('dashboard.myActivities.openJob')}
              >
                <ExternalLink className="w-4 h-4 t-muted" />
              </a>
            )}
          </div>
        );
      },
    },
  ], [t, i18n.language, sendingReminder, outcomeMap, savingOutcome, handleOutcomeChange, handleIterate]);

  // ── Initial-load skeleton ────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center w-full" style={{ minHeight: 'calc(100vh - 48px)' }}>
        <LoadingSpinner size="lg" showText={false} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ds-page p-4 sm:p-6 space-y-4">
        <PageHeader />
        <div className="ds-recessed-warn flex items-start gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="t-card-title">{t('dashboard.myActivities.couldNotLoad')}</p>
            <p className="t-meta mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-tour="page-activity" className="ds-page p-4 sm:p-6 space-y-4" style={{ isolation: 'isolate', pointerEvents: 'auto' }}>
      <PageHeader />

      {/* Filter chips above the table; `<md` → wrap to multi-row. */}
      <div data-tour="activity-filters">
      <FilterChips
        filters={filters}
        activeValues={filterValues}
        onChange={(id, vals) =>
          setFilterValues((prev) => ({ ...prev, [id]: vals }))
        }
        onClearAll={() => setFilterValues({})}
      />
      </div>

      {/* Desktop ≥md: Mobbin/Deel-density data table. */}
      <div data-tour="activity-table" className="hidden md:block">
        <DataTable<ActivityRow>
          columns={columns}
          rows={filteredRows}
          onFilterChange={() => {}}
          ariaLabel={t('dashboard.myActivities.title')}
          emptyMessage={t('dashboard.myActivities.noActivitiesYet')}
          pageSizeOptions={[10, 25, 50, 100]}
          initialPageSize={10}
        />
      </div>

      {/* Mobile <md: card list (table-as-cards per project mobile rule). */}
      <div className="md:hidden space-y-2">
        {filteredRows.length === 0 ? (
          <div
            className="ds-card t-meta text-center"
          >
            {t('dashboard.myActivities.noActivitiesYet')}
          </div>
        ) : (
          filteredRows.map((row) => (
            <MobileRowCard
              key={row.id}
              row={row}
              t={t}
              lang={i18n.language}
              sendingReminder={sendingReminder}
              outcomeMap={outcomeMap}
              savingOutcome={savingOutcome}
              onPreviewResume={handlePreviewResume}
              onPreviewCover={handlePreviewCover}
              onPreviewApplication={handlePreviewApplication}
              onSendReminder={sendReminderEmail}
              onOutcomeChange={handleOutcomeChange}
              onIterate={handleIterate}
            />
          ))
        )}
      </div>

      {/* Artifact Drawer — slide-in panel from the right with tabs for all three
          artifacts (Resume, Cover Letter, Application Note). Replaces the small
          centered modal that previously showed only one artifact at a time. */}
      <ArtifactDrawer
        preview={preview}
        onClose={() => setPreview(null)}
        onTabChange={(tab) => setPreview((p) => p ? { ...p, activeTab: tab } : p)}
      />

      {/* ── TASK 4-1: Toast strip ── */}
      <ToastStrip toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Artifact Drawer — slide-in panel with Resume / Cover Letter / App Note tabs
// ─────────────────────────────────────────────────────────────────────

interface ArtifactPreview {
  jobTitle: string;
  company: string;
  activeTab: 'resume' | 'cover' | 'application';
  resume: string | null;
  cover: string | null;
  application: string | null;
  loadingResume: boolean;
}

const ArtifactDrawer: React.FC<{
  preview: ArtifactPreview | null;
  onClose: () => void;
  onTabChange: (tab: 'resume' | 'cover' | 'application') => void;
}> = ({ preview, onClose, onTabChange }) => {
  // Close on Esc
  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, onClose]);

  if (!preview) return null;

  const content = preview.activeTab === 'resume'
    ? preview.resume
    : preview.activeTab === 'cover'
      ? preview.cover
      : preview.application;

  const emptyMessage = preview.activeTab === 'resume'
    ? (preview.loadingResume ? 'Loading optimized resume…' : 'No optimized resume saved for this job.')
    : preview.activeTab === 'cover'
      ? 'No cover letter saved for this job yet.'
      : 'No application note saved for this job yet.';

  const tabLabel = preview.activeTab === 'resume'
    ? 'Resume' : preview.activeTab === 'cover'
      ? 'Cover Letter' : 'Application Note';

  const copyContent = () => {
    if (!content) return;
    void navigator.clipboard.writeText(content);
  };

  const downloadContent = () => {
    if (!content) return;
    const safeJob = (preview.jobTitle || 'Document').replace(/[^a-zA-Z0-9]/g, '_');
    const fileName = `${tabLabel.replace(/\s+/g, '')}_${safeJob}.txt`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const tabs: Array<{ id: 'resume' | 'cover' | 'application'; label: string; icon: React.ReactNode; has: boolean }> = [
    { id: 'resume', label: 'Resume', icon: <FileText className="w-3.5 h-3.5" />, has: !!preview.resume || preview.loadingResume },
    { id: 'cover', label: 'Cover Letter', icon: <Mail className="w-3.5 h-3.5" />, has: !!preview.cover },
    { id: 'application', label: 'Application Note', icon: <Send className="w-3.5 h-3.5" />, has: !!preview.application },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 z-[61] h-full w-full md:w-[640px] flex flex-col shadow-2xl"
        style={{ background: 'var(--ds-bg, #ffffff)', borderLeft: '1px solid var(--ds-border)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Application artifacts"
      >
        {/* Header */}
        <div
          className="flex items-start justify-between gap-3 px-5 py-4 border-b shrink-0"
          style={{ borderColor: 'var(--ds-divider)' }}
        >
          <div className="min-w-0 flex-1">
            <div className="t-meta t-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700 }}>
              Saved Application
            </div>
            <h2 className="t-card-title truncate mt-0.5" style={{ fontSize: 16, fontWeight: 700 }}>
              {preview.jobTitle}
            </h2>
            {preview.company && (
              <div className="t-meta t-muted truncate mt-0.5" style={{ fontSize: 12 }}>
                {preview.company}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ds-btn ds-btn-ghost shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            aria-label="Close drawer"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab bar */}
        <div
          className="flex items-center gap-1 px-3 pt-2 border-b shrink-0"
          style={{ borderColor: 'var(--ds-divider)' }}
          role="tablist"
        >
          {tabs.map((tab) => {
            const isActive = preview.activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onTabChange(tab.id)}
                className="flex items-center gap-1.5 flex-shrink-0 min-h-[44px] px-3 py-2 transition-colors whitespace-nowrap touch-manipulation"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: isActive ? 'var(--brand-cta-bg, #5F8568)' : tab.has ? 'var(--ds-text)' : 'var(--ds-text-muted)',
                  borderBottom: isActive ? '2px solid var(--brand-cta-bg, #5F8568)' : '2px solid transparent',
                  marginBottom: -1,
                  opacity: tab.has ? 1 : 0.55,
                }}
              >
                {tab.icon}
                {tab.label}
                {!tab.has && <span style={{ fontSize: 9, opacity: 0.6 }}>(empty)</span>}
              </button>
            );
          })}
        </div>

        {/* Action row */}
        {content && (
          <div className="flex items-center justify-end gap-2 px-5 py-2 border-b shrink-0" style={{ borderColor: 'var(--ds-divider)' }}>
            <button
              type="button"
              onClick={copyContent}
              className="ds-btn ds-btn-ghost ds-btn-sm flex items-center gap-1.5"
              title="Copy to clipboard"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy
            </button>
            <button
              type="button"
              onClick={downloadContent}
              className="ds-btn ds-btn-secondary ds-btn-sm flex items-center gap-1.5"
              title="Download as .txt"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </button>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-auto p-6" style={{ background: 'var(--ds-bg-tinted, #f9fafb)' }}>
          {content ? (
            <div
              className="rounded-lg p-6 shadow-sm"
              style={{
                background: '#ffffff',
                border: '1px solid var(--ds-border)',
                fontFamily: 'var(--ds-font-sans)',
                fontSize: 13,
                lineHeight: 1.65,
                color: '#111827',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {content}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12">
              <div
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'var(--ds-bg-card)',
                  border: '1px dashed var(--ds-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16,
                }}
              >
                {preview.activeTab === 'resume' && <FileText className="w-7 h-7 t-muted" />}
                {preview.activeTab === 'cover' && <Mail className="w-7 h-7 t-muted" />}
                {preview.activeTab === 'application' && <Send className="w-7 h-7 t-muted" />}
              </div>
              <p className="t-body t-muted max-w-sm">{emptyMessage}</p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Page header (memoised so it doesn't re-render with table state)
// ─────────────────────────────────────────────────────────────────────

const PageHeader: React.FC = React.memo(function PageHeader() {
  const { t } = useTranslation();
  return (
    <div className="flex-shrink-0">
      <h1 className="t-page-title flex items-center gap-2 truncate min-w-0">
        <Activity className="w-6 h-6 sm:w-7 sm:h-7 t-brand shrink-0" />
        <span>{t('dashboard.myActivities.title')}</span>
      </h1>
      <p className="t-meta mt-0.5 md:mt-1 line-clamp-2">
        {t('dashboard.myActivities.subtitle')}
      </p>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────
// Toast strip (TASK 4-1 — lightweight, fixed bottom-center)
// ─────────────────────────────────────────────────────────────────────

const ToastStrip: React.FC<{
  toasts: ToastMsg[];
  onDismiss: (id: number) => void;
}> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed bottom-6 inset-x-0 z-[2000] flex flex-col items-center gap-2 pointer-events-none"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto flex items-center gap-2 transition-all
            ${toast.kind === 'error'
              ? 'ds-recessed-danger'
              : 'ds-card-sm'}
          `}
        >
          <span className="t-body">{toast.message}</span>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            className="ds-btn ds-btn-ghost ds-btn-sm shrink-0 touch-manipulation"
            aria-label="Dismiss notification"
          >
            <X className="w-3.5 h-3.5 t-muted" />
          </button>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Mobile row card (per `<md` table-as-card rule) — TASK 4-1 updated
// ─────────────────────────────────────────────────────────────────────

interface MobileRowCardProps {
  row:                  ActivityRow;
  t:                    ReturnType<typeof useTranslation>['t'];
  lang:                 string;
  sendingReminder:      string | null;
  outcomeMap:           Record<string, OptimizationOutcome>;
  savingOutcome:        string | null;
  onPreviewResume:      (app: JobApplication) => void | Promise<void>;
  onPreviewCover:       (app: JobApplication) => void;
  onPreviewApplication: (app: JobApplication) => void;
  onSendReminder:       (id: string) => void | Promise<void>;
  onOutcomeChange:      (histId: string, outcome: OptimizationOutcome) => void | Promise<void>;
  onIterate:            (row: ActivityRow) => void;
}

const MobileRowCard: React.FC<MobileRowCardProps> = ({
  row,
  t,
  lang,
  sendingReminder,
  outcomeMap,
  savingOutcome,
  onPreviewResume,
  onPreviewCover,
  onPreviewApplication,
  onSendReminder,
  onOutcomeChange,
  onIterate,
}) => {
  const app = row.raw;
  const hasReminder = (app as { reminder_date?: string }).reminder_date != null;
  const dateLabel = row.date ? new Date(row.date).toLocaleDateString(lang) : '—';
  const currentOutcome = row.optimHistoryId
    ? (outcomeMap[row.optimHistoryId] ?? 'unknown')
    : null;
  const isSaving = row.optimHistoryId ? savingOutcome === row.optimHistoryId : false;

  return (
    <article
      className="ds-tile flex-col items-stretch gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="t-card-title truncate" title={row.job}>{row.job}</p>
          <p className="t-meta truncate" title={row.company}>{row.company}</p>
        </div>
        <span
          className={`shrink-0 ${row.action === 'Optimized' ? 'ds-chip ds-chip-brand' : 'ds-chip'}`}
        >
          {row.action === 'Optimized'
            ? t('dashboard.myActivities.actionOptimized', 'Optimized')
            : t('dashboard.myActivities.actionApplied', 'Applied')}
        </span>
      </div>

      <div className="t-meta flex flex-wrap items-center gap-3">
        <span>{dateLabel}</span>
        {row.score != null && (
          <span
            className="t-card-title t-num"
            style={{ color: scoreTone(row.score) }}
          >
            {t('dashboard.myActivities.scoreColumn', 'Score')}: {Math.round(row.score)}
          </span>
        )}

        {/* TASK 4-1: outcome select on mobile for optimized rows */}
        {row.optimHistoryId && currentOutcome !== null ? (
          <div className="flex items-center gap-1">
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: OUTCOME_COLOR[currentOutcome] }}
              aria-hidden
            />
            <select
              value={currentOutcome}
              disabled={isSaving}
              aria-label={`Outcome for ${row.job}`}
              onChange={(e) =>
                void onOutcomeChange(row.optimHistoryId!, e.target.value as OptimizationOutcome)
              }
              className="ds-select w-auto disabled:opacity-50"
            >
              {OUTCOME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {isSaving && <LoadingSpinner size="xs" showText={false} />}
          </div>
        ) : (
          <span className="ds-chip truncate">{row.outcome}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1 pt-1 border-t" style={{ borderColor: 'var(--ds-divider)' }}>
        <button
          type="button"
          onClick={() => void onPreviewResume(app)}
          className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
        >
          <Eye className="w-4 h-4" /> {t('dashboard.myActivities.resume')}
        </button>
        <button
          type="button"
          onClick={() => onPreviewCover(app)}
          className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
        >
          <Mail className="w-4 h-4" /> {t('dashboard.myActivities.coverLetter')}
        </button>
        <button
          type="button"
          onClick={() => onPreviewApplication(app)}
          className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
        >
          <Share2 className="w-4 h-4" /> {t('dashboard.myActivities.application')}
        </button>

        {/* TASK 4-1: Iterate button on mobile */}
        {row.optimHistoryId && (
          <button
            type="button"
            onClick={() => onIterate(row)}
            className="ds-btn ds-btn-link min-h-[44px] touch-manipulation"
          >
            <GitBranch className="w-4 h-4" /> Iterate
          </button>
        )}

        {hasReminder && (
          <button
            type="button"
            disabled={!!sendingReminder}
            onClick={() => void onSendReminder(app.id)}
            className="ds-btn ds-btn-link min-h-[44px] touch-manipulation"
          >
            {sendingReminder === app.id
              ? <LoadingSpinner size="xs" showText={false} />
              : <Mail className="w-4 h-4" />}
            {t('dashboard.myActivities.sendEmail')}
          </button>
        )}
        {app.job_url && (
          <a
            href={app.job_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-btn ds-btn-link min-h-[44px] ml-auto touch-manipulation"
          >
            <ExternalLink className="w-4 h-4" /> {t('dashboard.myActivities.openJob')}
          </a>
        )}
      </div>
    </article>
  );
};
